
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useOrganization } from '@/contexts/OrganizationContext';

export interface EventGuest {
  id: string;
  event_id: string;
  full_name: string;
  handle: string;
  email: string;
  phone: string;
  guest_type: string;
  rsvp_status: string;
  category_tags: string[];
  notes: string;
  organization_id: string;
  created_at: string;
  updated_at: string;
}

export const useEventGuests = (eventId: string) => {
  return useQuery({
    queryKey: ['event_guests', eventId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('event_guests')
        .select('*')
        .eq('event_id', eventId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as EventGuest[];
    },
    enabled: !!eventId,
  });
};

export const useCreateEventGuest = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { currentOrganization } = useOrganization();

  return useMutation({
    mutationFn: async (guestData: Omit<EventGuest, 'id' | 'created_at' | 'updated_at' | 'organization_id'>) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');
      
      if (!currentOrganization?.id) throw new Error('No organization selected');

      const { data, error } = await supabase
        .from('event_guests')
        .insert([{
          ...guestData,
          organization_id: currentOrganization.id
        }])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['event_guests', data.event_id] });
      toast({
        title: "Success",
        description: "Guest added successfully",
      });
    },
    onError: (error) => {
      console.error('Guest creation error:', error);
      toast({
        title: "Error",
        description: "Failed to add guest",
        variant: "destructive",
      });
    },
  });
};

export const useUpdateEventGuest = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, ...guestData }: Partial<EventGuest> & { id: string }) => {
      const { data, error } = await supabase
        .from('event_guests')
        .update(guestData)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['event_guests', data.event_id] });
      toast({
        title: "Success",
        description: "Guest updated successfully",
      });
    },
    onError: (error) => {
      console.error('Guest update error:', error);
      toast({
        title: "Error",
        description: "Failed to update guest",
        variant: "destructive",
      });
    },
  });
};

export const useDeleteEventGuest = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, eventId }: { id: string; eventId: string }) => {
      const { error } = await supabase
        .from('event_guests')
        .delete()
        .eq('id', id);

      if (error) throw error;
      return { eventId };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['event_guests', data.eventId] });
      toast({
        title: "Success",
        description: "Guest deleted successfully",
      });
    },
    onError: (error) => {
      console.error('Guest deletion error:', error);
      toast({
        title: "Error",
        description: "Failed to delete guest",
        variant: "destructive",
      });
    },
  });
};
